/**
 * Session and role routing. OWNER: web-frontend-engineer.
 *
 * ONE DOOR, ROLE-ROUTED. The destination is decided SERVER-SIDE from the role on
 * the session (`SessionResponse.landing`) — never from a form field, a URL or a
 * toggle. There is no role switcher in this app, and there is no place to add
 * one: `landing` is read, never computed.
 *
 * `ST-SIGNIN-ALREADYIN`: a valid session redirects to the role's landing BEFORE
 * FIRST PAINT — the door never flashes.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { SessionResponse } from '@pt/core';
import { api } from '../lib/api.ts';
import type { HouseContact, PersonRef } from '../lib/contracts.ts';

export type SessionPhase = 'resolving' | 'anonymous' | 'signed-in';

interface SessionValue {
  readonly phase: SessionPhase;
  readonly session: SessionResponse | null;
  readonly signIn: (session: SessionResponse) => void;
  readonly signOut: () => Promise<void>;
  /** `ST-SIGNIN-EXPIRED` — the session ended, so the record was not left open. */
  readonly expire: () => void;
  readonly expired: boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

/** The named people a screen needs: the client's tailor and the house contact. */
interface HouseValue {
  readonly contact: HouseContact | null;
  readonly tailor: PersonRef | null;
  readonly remember: (next: { contact?: HouseContact | null; tailor?: PersonRef | null }) => void;
}

const HouseContext = createContext<HouseValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<SessionPhase>('resolving');
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [expired, setExpired] = useState(false);
  const [contact, setContact] = useState<HouseContact | null>(null);
  const [tailor, setTailor] = useState<PersonRef | null>(null);

  useEffect(() => {
    let live = true;
    api
      .currentSession()
      .then((current) => {
        if (!live) return;
        setSession(current);
        setPhase('signed-in');
      })
      .catch(() => {
        if (!live) return;
        setSession(null);
        setPhase('anonymous');
      });
    return () => {
      live = false;
    };
  }, []);

  const signIn = useCallback((next: SessionResponse) => {
    setSession(next);
    setExpired(false);
    setPhase('signed-in');
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } catch {
      // Signing out locally is still correct: the record must not be left open.
    }
    setSession(null);
    setPhase('anonymous');
    window.history.pushState(null, '', '/signin?state=signedout');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const expire = useCallback(() => {
    setSession(null);
    setExpired(true);
    setPhase('anonymous');
  }, []);

  const remember = useCallback(
    (next: { contact?: HouseContact | null; tailor?: PersonRef | null }) => {
      if (next.contact !== undefined && next.contact !== null) setContact(next.contact);
      if (next.tailor !== undefined && next.tailor !== null) setTailor(next.tailor);
    },
    [],
  );

  const sessionValue = useMemo(
    () => ({ phase, session, signIn, signOut, expire, expired }),
    [phase, session, signIn, signOut, expire, expired],
  );
  const houseValue = useMemo(() => ({ contact, tailor, remember }), [contact, tailor, remember]);

  return (
    <SessionContext.Provider value={sessionValue}>
      <HouseContext.Provider value={houseValue}>{children}</HouseContext.Provider>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSession outside SessionProvider');
  return value;
}

export function useHouse(): HouseValue {
  const value = useContext(HouseContext);
  if (value === null) return { contact: null, tailor: null, remember: () => {} };
  return value;
}
